<template>
  <div v-if="isAuthenticated && !isInSetup()">
    <h1>You are already logged in</h1>
    <p>Please logout to register a new account</p>
    <button
      @click="logout"
      class="btn-primary"
    >
      Logout
    </button>
  </div>
  <div
    class="registration-container"
    v-else-if="showInvitationOrWaitingList"
  >
    <div class="registration-alternatives">
      <RegisterByInvitationCode
        v-if="isRegistrationByInvitationEnabled"
        @code-validated="onInvitationCodeValidated"
      />
      <WaitingListSubscribe
        v-if="isRegistrationByWaitingListEnabled"
        @subscribed="onWaitingListSubscribed"
      />
    </div>
    <div class="registration-alternatives-login">
      <button
        @click="() => router.push('/login')"
        class="btn-secondary"
      >
        {{ t('registration.introduction.alreadyHaveAccount') }}
      </button>
    </div>
  </div>
  <div
    class="registration-container"
    v-else-if="!isRegistrationByEmailEnabled && !validInvitationCode && !hasValidStoredToken"
  >
    <h1>Registration is not enabled</h1>
    <p>Please contact the administrator to enable registration</p>
  </div>
  <div
    class="registration-container"
    v-else
  >
    <!-- Resume Dialog -->
    <div
      v-if="showResumeDialog"
      class="resume-dialog-overlay"
    >
      <div class="resume-dialog">
        <h3>Resume Registration</h3>
        <p>There is a pending registration for <strong>{{ profileData.username }}</strong>. Do you want to resume it or discard it?</p>
        <div class="dialog-actions">
          <button
            @click="resumeRegistration"
            class="btn-primary"
          >
            Resume
          </button>
          <button
            @click="discardRegistration"
            class="btn-secondary"
          >
            Discard
          </button>
        </div>
      </div>
    </div>

    <div class="registration-header">
      <!-- <h1>{{ t('registration.title') }}</h1> -->
      <div class="progress-bar">
        <div
          class="progress-step"
          :class="{ active: isStepIntroductionActive(), completed: isStepIntroductionDone() }"
        >
          <span
            class="step-label clickable"
            role="button"
            tabindex="0"
            @click="!isStepRegistrationCompleteActive() && goToIntroduction()"
            @keydown.enter.prevent="goToIntroduction"
            @keydown.space.prevent="goToIntroduction"
          >
            {{ t('registration.step_introduction.title') }}
          </span>
        </div>
        <div
          class="progress-line"
          :class="{ completed: isStepIntroductionDone() }"
        />
        <div
          class="progress-step"
          :class="{ active: isStepAccountSetupActive(), completed: isStepAccountSetupDone() }"
        >
          <span
            class="step-label clickable"
            role="button"
            tabindex="0"
            @click="!isStepRegistrationCompleteActive() && goToAccountSetup()"
            @keydown.enter.prevent="goToAccountSetup"
            @keydown.space.prevent="goToAccountSetup"
          >
            {{ t('registration.step_account_setup.title') }}
          </span>
        </div>
        <div
          class="progress-line"
          :class="{ completed: isStepAccountSetupDone() }"
        />
        <div
          class="progress-step"
          :class="{ active: isStepAccountVerificationActive(), completed: isStepAccountSetupDone() }"
        >
          <span class="step-label">{{ t('registration.step_account_verification.title') }}</span>
        </div>
        <div
          class="progress-line"
          :class="{ completed: isStepAccountVerificationDone() }"
        />
        <div
          class="progress-step"
          :class="{ active: isStepAccountRegistrationActive(), completed: isStepAccountRegistrationDone() }"
        >
          <span class="step-label">{{ t('registration.step_registration_complete.title') }}</span>
        </div>
      </div>
    </div>

    <div class="registration-content">
      <!-- Step 0: Introduction -->
      <div
        v-if="currentStep === 'introduction'"
        class="step-content"
      >
        <RegistrationIntroductionView 
          @start-registration="goToAccountSetup"
          @go-to-login="() => router.push('/login')"
        />
      </div>

      <!-- Step 1: Profile Setup -->
      <div
        v-if="currentStep === 'account_setup'"
        class="step-content"
      >
        <!-- <h2>{{ t('registration.step_account_setup.title') }}</h2> -->
        <form @submit.prevent="nextStep">
          <div class="form-row">
            <!-- <div class="form-group">
              <label for="username">{{ t('registration.step_account_setup.username') }} *</label>
              <input
                type="text"
                id="username"
                v-model="profileData.username"
                @input="clearStepAccountSetupError"
                required
                :placeholder="t('registration.step_account_setup.usernamePlaceholder')"
              >
            </div> -->
            <div class="form-group">
              <label for="email">{{ t('registration.step_account_setup.email') }} *</label>
              <div class="email-input-wrapper">
                <input
                  type="email"
                  id="email"
                  class="email-input"
                  :class="{ 
                    'input-error': emailValidationState === 'invalid',
                    'input-valid': emailValidationState === 'valid'
                  }"
                  v-model="profileData.email"
                  @input="validateEmailInput"
                  required
                  :placeholder="t('registration.step_account_setup.emailPlaceholder')"
                >
                <div class="email-validation-icon">
                  <svg
                    v-if="emailValidationState === 'valid'"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="icon-valid"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  <svg
                    v-else-if="emailValidationState === 'invalid' && profileData.email"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="icon-invalid"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                    />
                    <line
                      x1="12"
                      y1="8"
                      x2="12"
                      y2="12"
                    />
                    <line
                      x1="12"
                      y1="16"
                      x2="12.01"
                      y2="16"
                    />
                  </svg>
                </div>
              </div>
              <div
                v-if="emailValidationError"
                class="input-error"
                style="margin-top: 5px; font-size: 0.9em;"
              >
                {{ emailValidationError }}
              </div>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="password">{{ t('registration.step_account_setup.password') }} *</label>
              <div class="password-input">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  id="password"
                  v-model="profileData.password"
                  @input="clearStepAccountSetupError"
                  required
                  autocomplete="new-password"
                >
                <button
                  type="button"
                  class="toggle-visibility"
                  @click="showPassword = !showPassword"
                  :aria-pressed="showPassword ? 'true' : 'false'"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                  tabindex="-1"
                >
                  <span aria-hidden="true">
                    <svg
                      v-if="!showPassword"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 11-7-4-7-11-7zm0 12c-2.761 0-5-2.239-5-5s2.239-5 5-5 5 2.239 5 5-2.239 5-5 5zm0-8a3 3 0 100 6 3 3 0 000-6z" />
                    </svg>
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M2.293 2.293l19.414 19.414-1.414 1.414-3.088-3.088C14.966 21.06 13.522 21 12 21 5 21 1 14 1 14s1.44-2.525 4.253-4.776L.879 3.707l1.414-1.414zM12 7c-1.208 0-2.313.43-3.172 1.146l1.493 1.493A2.99 2.99 0 0112 9a3 3 0 012.829 4.001l1.493 1.493A4.99 4.99 0 0017 12c0-2.761-2.239-5-5-5zm11 7s-1.088 1.904-3.033 3.763l-1.435-1.435C20.293 14.77 21 14 21 14s-4-7-11-7c-.498 0-.98.036-1.446.104l-1.58-1.58C7.886 5.18 9.86 5 12 5c7 0 11 7 11 7z" />
                    </svg>
                  </span>
                </button>
              </div>
            </div>
            <div class="form-group">
              <label for="confirmPassword">{{ t('registration.step_account_setup.confirmPassword') }} *</label>
              <div class="password-input">
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  id="confirmPassword"
                  v-model="profileData.confirmPassword"
                  @input="clearStepAccountSetupError"
                  required
                  autocomplete="new-password"
                  :class="{ invalid: passwordsDoNotMatch }"
                  :aria-invalid="passwordsDoNotMatch ? 'true' : 'false'"
                >
                <button
                  type="button"
                  class="toggle-visibility"
                  @click="showConfirmPassword = !showConfirmPassword"
                  :aria-pressed="showConfirmPassword ? 'true' : 'false'"
                  :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
                  tabindex="-1"
                >
                  <span aria-hidden="true">
                    <svg
                      v-if="!showConfirmPassword"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 5c-7 0-11 7-11 7s4 7 11 7 11-7 11-7-4-7-11-7zm0 12c-2.761 0-5-2.239-5-5s2.239-5 5-5 5 2.239 5 5-2.239 5-5 5zm0-8a3 3 0 100 6 3 3 0 000-6z" />
                    </svg>
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M2.293 2.293l19.414 19.414-1.414 1.414-3.088-3.088C14.966 21.06 13.522 21 12 21 5 21 1 14 1 14s1.44-2.525 4.253-4.776L.879 3.707l1.414-1.414zM12 7c-1.208 0-2.313.43-3.172 1.146l1.493 1.493A2.99 2.99 0 0112 9a3 3 0 012.829 4.001l1.493 1.493A4.99 4.99 0 0017 12c0-2.761-2.239-5-5-5zm11 7s-1.088 1.904-3.033 3.763l-1.435-1.435C20.293 14.77 21 14 21 14s-4-7-11-7c-.498 0-.98.036-1.446.104l-1.58-1.58C7.886 5.18 9.86 5 12 5c7 0 11 7 11 7z" />
                    </svg>
                  </span>
                </button>
              </div>
              <div
                v-if="passwordsDoNotMatch"
                class="input-error"
              >
                {{ t('registration.step_account_setup.passwordsDoNotMatch') }}
              </div>
            </div>
          </div>
          <div
            v-for="field in profile_fields"
            :key="field.key"
          >
            <div class="form-group">
              <label for="field.key">{{ t('profileSetup.' + field.label) }}</label>
              <input
                :type="field.type"
                :id="field.key"
                v-model="profileData.profile[field.key]"
                :placeholder="t('profileSetup.' + field.label + 'Placeholder')"
              >
              <small
                v-if="t('profileSetup.' + field.label + 'Help', '')"
                class="field-help"
              >{{ t('profileSetup.' + field.label + 'Help', '') }}</small>                
            </div>
          </div>
          
          <div
            v-if="stepAccountSetupError"
            class="error-message"
          >
            {{ stepAccountSetupError }}
          </div>
          <div
            v-if="showResendActivationFromSetup"
            class="resend-activation-action"
          >
            <button
              type="button"
              @click="requestResendActivationFromSetup"
              class="btn-secondary"
              :disabled="isResendingActivationFromSetup"
            >
              {{ isResendingActivationFromSetup ? t('registration.step_account_setup.goingToVerification') : t('registration.step_account_setup.goToAccountVerification') }}
            </button>
          </div>

          <div class="form-actions">
            <button 
              type="submit" 
              class="btn-primary" 
              :disabled="isSubmitting || !isValidEmail(profileData.email)"
            >
              {{ isSubmitting ? t('registration.step_account_setup.submitting') : t('registration.step_account_setup.submit') }}
            </button>
          </div>
        </form>
      </div>

      <!-- Step 2: Account Verification -->
      <div
        v-if="currentStep === 'account_verification'"
        class="step-content"
      >
        <h2>{{ t('registration.step_account_verification.title') }}</h2>  
        <p class="step-description">
          {{ t('registration.step_account_verification.checkEmail') }}
        </p>
        
        <div class="verification-info">
          <p class="email-info">
            {{ t('registration.step_account_verification.emailSentTo') }} <strong>{{ profileData.email }}</strong>
          </p>
        </div>

        <div class="verification-actions">
          <button 
            @click="verifyAccountActivation" 
            class="btn-primary" 
            :disabled="isCheckingActivation"
          >
            <span
              v-if="isCheckingActivation"
              class="btn-spinner"
            />
            <span
              v-else
              class="btn-icon"
            >✓</span>
            {{ isCheckingActivation ? t('registration.step_account_verification.checkingActivation') : t('registration.step_account_verification.verifyAccount') }}
          </button>
          <button 
            @click="resendActivationEmail" 
            class="btn-secondary" 
            :disabled="isResendingActivation"
          >
            {{ isResendingActivation ? t('registration.step_account_verification.sendingEmail') : t('registration.step_account_verification.resendEmail') }}
          </button>
          <button 
            @click="cancelRegistrationFromVerification" 
            class="btn-secondary"
          >
            {{ t('registration.step_account_verification.cancelRegistration') }}
          </button>
        </div>

        <div
          v-if="activationInformation"
          class="activation-information"
          :class="{ 'activation-information-success': activationInformationType === 'success', 'activation-information-error': activationInformationType === 'error' }"
        >
          <span
            v-if="activationInformationType === 'success'"
            class="info-icon"
          >✓</span>
          <span
            v-else-if="activationInformationType === 'error'"
            class="info-icon"
          >⚠</span>
          <p>{{ activationInformation }}</p>
        </div>

        <div
          v-if="activationError"
          class="error-message"
        >
          {{ activationError }}
        </div>

        <div
          v-if="activationSuccess"
          class="success-message"
        >
          <span class="success-icon">✓</span>
          {{ t('registration.step_account_verification.accountVerified') }} {{ t('registration.step_account_verification.redirecting') }}
        </div>
      </div>

      <!-- Step 3: Registration Complete -->
      <div
        v-if="currentStep === 'registration_complete'"
        class="step-content"
      >
        <h2>{{ t('registration.step_registration_complete.title') }}</h2>
        <div class="completion-message">
          <span class="success-icon">🎉</span>
          <p class="completion-message-text">
            {{ t('registration.step_registration_complete.accountCreatedAndVerified') }}
          </p>
        </div>

        <div class="registration-summary">
          <h3>{{ t('registration.step_registration_complete.registrationSummary') }}</h3>
          <div class="summary-item">
            <span class="summary-label">Profile created:</span>
            <span class="summary-value">{{ profileData.username }} ({{ profileData.email }})</span>
          </div>
          <div
            v-for="field in profile_fields"
            :key="field.key"
          >
            <div class="summary-item">
              <span class="summary-label">{{ field.label }}:</span>
              <span class="summary-value">{{ profileData.profile[field.key] || 'Not provided' }}</span>
            </div>
          </div>
        </div>

        <div class="completion-actions">
          <button
            @click="redirectToDashboard"
            class="btn-primary"
            v-if="isAuthenticated"
          >
            {{ t('registration.step_registration_complete.continueToDashboard') }}
          </button>
          <button
            @click="redirectToLogin"
            class="btn-secondary"
            v-else
          >
            {{ t('registration.step_registration_complete.continueToLogin') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { r7eBaseApp, DEBUG_MODE } from '@r7e/shared';
import RegistrationIntroductionView from './RegistrationIntroductionView.vue';
import RegisterByInvitationCode from '../components/RegisterByInvitationCode.vue';
import WaitingListSubscribe from '../components/WaitingListSubscribe.vue';
const { t } = useI18n();
const router = useRouter();


// registration state
const currentStep = ref('introduction');
const STEP_STORAGE_KEY = 'registration.currentStep';
const USERNAME_STORAGE_KEY = 'registration.username';
const EMAIL_STORAGE_KEY = 'registration.email';
const REG_TOKEN_STORAGE_KEY = 'registration.registration_token';

const profile_fields = [
  { key: 'name', label: 'displayName', type: 'text' },  
  // { key: 'affiliation', label: 'affiliation', type: 'text'},  
  // { key: 'home_page', label: 'homePage', type: 'url' },
  // { key: 'orcid', label: 'orcid', type: 'text' },
  // { key: 'linkedin', label: 'linkedin', type: 'text' },
  // { key: 'google_scholar', label: 'googleScholar', type: 'text' },
  // { key: 'avatar_url', label: 'avatarUrl', type: 'text' },
]

// Step 1: Profile data
const profileData = ref({
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  profile: {}
});

const isAuthenticated = ref(false);
const isRegistrationByInvitationEnabled = ref(false);
const isRegistrationByWaitingListEnabled = ref(false);
const isRegistrationByEmailEnabled = ref(false);
const validInvitationCode = ref('');

const hasValidStoredToken = ref(false);

const showInvitationOrWaitingList = computed(() =>
  !isRegistrationByEmailEnabled.value && !validInvitationCode.value && !hasValidStoredToken.value &&
  (isRegistrationByInvitationEnabled.value || isRegistrationByWaitingListEnabled.value)
);

function onInvitationCodeValidated(code) {
  validInvitationCode.value = code;
  profileData.value.invitation_code = code;
  currentStep.value = 'account_setup';
}

function onWaitingListSubscribed() {
  // Optional: could show a toast or navigate
}

async function fetchAccountServiceSettings(service) {
  const settings = await r7eBaseApp.getAccountServiceSettings(service);
  return settings;
}

onMounted(async () => {
  const settings = await fetchAccountServiceSettings(r7eBaseApp.getCurrentService());
  isRegistrationByInvitationEnabled.value = settings.isRegistrationByInvitationEnabled;
  isRegistrationByWaitingListEnabled.value = settings.isRegistrationByWaitingListEnabled;
  isRegistrationByEmailEnabled.value = settings.isRegistrationByEmailEnabled;
});

// eslint-disable-next-line no-unused-vars
const on_authentication_event = (d) => {
  // console.log("RegisterView, authentication event", isAuthenticated.value);
  isAuthenticated.value = r7eBaseApp.isAuthenticated();
}

r7eBaseApp.registerCredentialsObserver(({ d }) => {
  // console.log("NavBar, credentials observer notified", username);
  isAuthenticated.value = r7eBaseApp.isAuthenticated();
  on_authentication_event (d);
});

// Restore step from storage on load
onMounted(() => {
  try {
    const savedStep = localStorage.getItem(STEP_STORAGE_KEY);
    const validSteps = ['introduction', 'account_setup', 'account_verification', 'registration_complete'];
    if (savedStep && validSteps.includes(savedStep)) {
      currentStep.value = savedStep;
    } else {
      // If no saved step or invalid step, start with introduction
      currentStep.value = 'introduction';
    }

    const savedUsername = localStorage.getItem(USERNAME_STORAGE_KEY);
    if (savedUsername) {
      profileData.value.username = savedUsername;
    }

    const savedEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
    if (savedEmail) {
      profileData.value.email = savedEmail;
    }

    const savedRegistrationToken = localStorage.getItem(REG_TOKEN_STORAGE_KEY);
    if (savedRegistrationToken) {
      profileData.value.registration_token = savedRegistrationToken;
    }

    // If we have registration_token and email, validate token and go directly to verification step
    if (savedRegistrationToken && savedEmail) {
      validateStoredTokenAndGoToVerification();
    } else {
      // Show resume dialog if we have stored data and not on introduction or account_setup step
      if ((savedUsername || savedEmail || savedRegistrationToken) && savedStep && savedStep !== 'introduction' && savedStep !== 'account_setup') {
        showResumeDialog.value = true;
      }
    }
  } catch (e) {
    // ignore storage errors
  }
});

// Validate stored registration token and go to verification step if valid
const validateStoredTokenAndGoToVerification = async () => {
  const email = profileData.value.email;
  const regToken = profileData.value.registration_token;
  if (!email || !regToken) return;

  try {
    const result = await r7eBaseApp.checkAccountActivation(email, regToken);
    if (result.status === 'error' && result.error === 'Invalid credentials') {
      // Token invalid, clear it
      profileData.value.registration_token = null;
      try { localStorage.removeItem(REG_TOKEN_STORAGE_KEY); } catch (e) { /* ignore */ }
    } else {
      // Token valid (accepted by API), go to verification step
      hasValidStoredToken.value = true;
      currentStep.value = 'account_verification';
      if (result.activated) {
        // Already activated - trigger verification flow which will handle completion
        await verifyAccountActivation();
      }
    }
  } catch (e) {
    // API error - token likely invalid, clear it
    profileData.value.registration_token = null;
    try { localStorage.removeItem(REG_TOKEN_STORAGE_KEY); } catch (err) { /* ignore */ }
  }
};

// Persist step on change
watch(currentStep, (newStep) => {
  try {
    if (!isStepRegistrationCompleteActive()) {
      localStorage.setItem(STEP_STORAGE_KEY, newStep);
    }
  } catch (e) {
    // ignore storage errors
  }
});

// Persist email on change
watch(
  () => profileData.value.email,
  (newEmail) => {
    if (!isStepRegistrationCompleteActive()) {
      try {
        if (newEmail) {
          localStorage.setItem(EMAIL_STORAGE_KEY, newEmail);
        } else {
          localStorage.removeItem(EMAIL_STORAGE_KEY);
        }
      } catch (e) {
          // ignore storage errors
      }
    }
  }
);
watch(
  () => profileData.value.registration_token,
  (newRegistrationToken) => { 
    if (!isStepRegistrationCompleteActive()) {
      try {
        if (newRegistrationToken) {
          localStorage.setItem(REG_TOKEN_STORAGE_KEY, newRegistrationToken);
        } else {
          localStorage.removeItem(REG_TOKEN_STORAGE_KEY);
        }
      } catch (e) {
        // ignore storage errors
      }
    }
  }
);

// Persist username on change
watch(
  () => profileData.value.username,
  (newUsername) => {
    if (!isStepRegistrationCompleteActive()) {
      try {
        if (newUsername) {
          localStorage.setItem(USERNAME_STORAGE_KEY, newUsername);
        } else {
          localStorage.removeItem(USERNAME_STORAGE_KEY);
        }
      } catch (e) {
          // ignore storage errors
      }
    }
  }
);

const logout = () => {
  r7eBaseApp.logoutAccountSync();
  // isAuthenticated.value = r7eBaseApp.isAuthenticated();
};

// Clear all stored registration data
const clearStoredRegistrationData = () => {
  hasValidStoredToken.value = false;
  profileData.value.registration_token = null;
  try {
    localStorage.removeItem(STEP_STORAGE_KEY);
    localStorage.removeItem(USERNAME_STORAGE_KEY);
    localStorage.removeItem(EMAIL_STORAGE_KEY);
    localStorage.removeItem(REG_TOKEN_STORAGE_KEY);
  } catch (e) {
    // ignore storage errors
  }
};

// Resume registration with stored data
const resumeRegistration = () => {
  showResumeDialog.value = false;
  if (isStepAccountVerificationDone()) {
    clearStoredRegistrationData();
  }
  // Data is already loaded in onMounted
};

// Cancel registration from verification step
const cancelRegistrationFromVerification = () => {
  const code = profileData.value.invitation_code;
  clearVerificationInterval();
  clearStoredRegistrationData();
  showResumeDialog.value = false;
  activationError.value = '';
  activationSuccess.value = false;
  activationInformation.value = '';
  currentStep.value = 'introduction';
  profileData.value = {
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    profile: {},
    invitation_code: code || undefined
  };
  router.push('/login');
};

// Discard stored data and restart
const discardRegistration = () => {
  const code = profileData.value.invitation_code;

  clearStoredRegistrationData();
  showResumeDialog.value = false;
  currentStep.value = 'account_setup';
  profileData.value = {
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    profile: {}
  };
  if (code) {
    profileData.value.invitation_code = code;
  }
  emailValidationError.value = '';
  emailValidationState.value = '';
};

const isSubmitting = ref(false);
const showResendActivationFromSetup = ref(false);
const isResendingActivationFromSetup = ref(false);

// Step 2: Account Verification state - Updated for activation check
const isCheckingActivation = ref(false);
const isResendingActivation = ref(false);
const activationError = ref('');
const activationSuccess = ref(false);
const activationInformation = ref('');
const activationInformationType = ref('success'); // 'success' or 'error'

const isInSetup = () => {
  return currentStep.value === 'introduction' || currentStep.value === 'account_setup' || currentStep.value === 'account_verification' || currentStep.value === 'registration_complete';
}
const isStepIntroductionActive = () => {
  return currentStep.value === 'introduction';
}
const isStepIntroductionDone = () => {
  return currentStep.value !== 'introduction';
}
const isStepAccountSetupActive = () => {
  return currentStep.value === 'account_setup';
}
const isStepAccountVerificationActive = () => {
  return currentStep.value === 'account_verification';
}
const isStepAccountRegistrationActive = () => {
  return currentStep.value === 'registration_complete';
}
const isStepRegistrationCompleteActive = () => {
  return currentStep.value === 'registration_complete';
}

const isStepAccountSetupDone = () => {
  return currentStep.value === 'account_verification' || currentStep.value === 'registration_complete';
}
const isStepAccountVerificationDone = () => {
  return currentStep.value === 'registration_complete';
}
const isStepAccountRegistrationDone = () => {
  return currentStep.value === 'registration_complete';
}

// UI state: show/hide password fields
const showPassword = ref(false);
const showConfirmPassword = ref(false);

// Resume dialog state
const showResumeDialog = ref(false);

// Live validation
const passwordsDoNotMatch = computed(() => {
  return (
    profileData.value.password.length > 0 &&
    profileData.value.confirmPassword.length > 0 &&
    profileData.value.password !== profileData.value.confirmPassword
  );
});

// Step 1: Local validation error
const stepAccountSetupError = ref('');
const emailValidationError = ref('');
const emailValidationState = ref(''); // 'valid', 'invalid', or ''

// Email validation function
const isValidEmail = (email) => {
  if (!email) return false;
  // RFC 5322 compliant email regex (simplified but covers most cases)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

// Real-time email validation on input
const validateEmailInput = () => {
  emailValidationError.value = '';
  stepAccountSetupError.value = '';
  if (!profileData.value.email) {
    emailValidationState.value = '';
    return;
  }
  if (isValidEmail(profileData.value.email)) {
    emailValidationState.value = 'valid';
  } else {
    emailValidationState.value = 'invalid';
  }
};


// Methods
const clearStepAccountSetupError = () => {
  stepAccountSetupError.value = '';
  emailValidationError.value = '';
  showResendActivationFromSetup.value = false;
};

const requestResendActivationFromSetup = async () => {
  const email = profileData.value.email;
  if (!email?.trim()) return;

  isResendingActivationFromSetup.value = true;
  stepAccountSetupError.value = '';
  showResendActivationFromSetup.value = false;

  try {
    const result = await r7eBaseApp.resendActivationEmail(email.trim());
    const regToken = result?.registration_token;
    if (regToken) {
      profileData.value.registration_token = regToken;
      await proceedToVerification(regToken);
    } else {
      stepAccountSetupError.value = result?.message || t('registration.step_account_setup.resendActivationFailed');
      showResendActivationFromSetup.value = true;
    }
  } catch (err) {
    const msg = err.response?.data?.message || err.message || t('registration.step_account_setup.resendActivationFailed');
    stepAccountSetupError.value = msg;
    console.log('requestResendActivationFromSetup set RESEND TO TRUE');
    showResendActivationFromSetup.value = true;
  } finally {
    isResendingActivationFromSetup.value = false;
  }
};

const nextStep = async () => {
  if (currentStep.value === 'account_setup') {
    // Clear any previous errors
    stepAccountSetupError.value = '';
    emailValidationError.value = '';
    showResendActivationFromSetup.value = false;
    
    // Validate email format
    if (!isValidEmail(profileData.value.email)) {
      emailValidationError.value = t('login.error.invalidEmailFormat');
      emailValidationState.value = 'invalid';
      stepAccountSetupError.value = t('login.error.invalidEmailFormat');
      return;
    }
    
    // Validate passwords
    if (profileData.value.password !== profileData.value.confirmPassword) {
      stepAccountSetupError.value = t('registration.step_account_setup.passwordsDoNotMatch');
      return;
    }
    
    // Call real backend registration
    try {
      const registrationPayload = {
        name: profileData.value.name,
        email: profileData.value.email,
        password: profileData.value.password,
        profile: profileData.value.profile
      };
      if (profileData.value.invitation_code) {
        registrationPayload.invitation_code = profileData.value.invitation_code;
      }
      
      // Debug logging to see what's being sent (password excluded for security)
      if (DEBUG_MODE) {
        console.log('=== REGISTRATION DEBUG ===');
        console.log('Profile Data (password excluded):', {
          ...profileData.value,
          password: '[PROTECTED]',
          confirmPassword: '[PROTECTED]'
        });
        const safePayload = {
          ...registrationPayload,
          password: '[PROTECTED]'
        };
        console.log('Registration Payload (password excluded):', safePayload);
        console.log('Profile Object:', registrationPayload.profile);
        console.log('JSON Stringified (password excluded):', JSON.stringify(safePayload, null, 2));
        console.log('========================');
      }
  
      const response = await r7eBaseApp.registerAccount(registrationPayload);
      let reg_token=null;
      if (response.registration_token) {
        reg_token = response.registration_token;
      }
      
      if (response.status === 'pending') {
        // Registration successful, proceed to verification step
        await proceedToVerification(reg_token);
      } else {
        // Handle other response types
        // console.log('Registration response:', response);
        // Even if response doesn't explicitly say "Waiting for activation", 
        // proceed to verification step for email confirmation
        await proceedToVerification(reg_token);
      }
    } catch (error) {
      console.error('=== REGISTRATION ERROR DEBUG ===');
      console.error('Full error object:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);
      console.error('================================');
      
      // Extract error message from various possible response formats
      let errorCode = '';
      let errorMessage = null;
     
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Check for different error message formats
        if (errorData.error) {
          errorCode = errorData.error;
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } else if (errorData.message) {
          errorCode = errorData.message;
        } else if (errorData.detail) {
          errorCode = errorData.detail;
        } else if (typeof errorData === 'string') {
          errorCode = errorData;
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          // Handle validation errors array
          errorCode = errorData.errors.join(', ');
        } else {
          // Fallback: stringify the error data
          errorCode = `Registration failed: ${JSON.stringify(errorData)}`;
        }
      } else if (error.message) {
        errorCode = error.message;
        errorMessage = error.message;
      } else {
        errorCode = 'Registration failed. Please try again.';
      }
      if (!errorMessage) {
        errorMessage = errorCode;
      }

      console.log('errorCode', errorCode);
      console.log('errorMessage', errorMessage);

      stepAccountSetupError.value = errorMessage;
      // Check if error indicates email already taken (pending registration) - offer resend activation
      const err = (errorCode || '').toLowerCase();
      if (err.includes('already') && (err.includes('email') && err.includes('waiting') && err.includes('verification'))) {
        console.log('requestResendActivationFromSetup set RESEND TO TRUE by error code');
        showResendActivationFromSetup.value = true
      } else if (err.includes('already') && (err.includes('email') && err.includes('taken') && err.includes('account'))) {
        showResendActivationFromSetup.value = false;
      }
      return;
    }
  } else {
    if (currentStep.value === 'account_verification') {
      goToAccountVerification();
    } else {
      goToRegistrationComplete();
    }
  }
};

const goToIntroduction = () => {
  currentStep.value = 'introduction';
};

const goToAccountSetup = () => {
  // console.log('goToAccountSetup');
  currentStep.value = 'account_setup';
  // also clear any activation state when navigating back
  activationError.value = '';
  activationSuccess.value = false;
  profileData.value.registration_token = null;
  emailValidationError.value = '';
  emailValidationState.value = '';
};

const goToAccountVerification = () => {
  currentStep.value = 'account_verification';
  activationInformation.value = '';
};

const goToRegistrationComplete = () => {
  currentStep.value = 'registration_complete';
  clearStoredRegistrationData();
};

// Account Verification Methods
const proceedToVerification = async (reg_token) => {
  // Reset activation state
  activationError.value = '';
  activationSuccess.value = false;
  activationInformation.value = '';
  
  // Move to verification step - show "Check your email" message
  // User must manually click "Check Activation" button after clicking email link
  goToAccountVerification();
  profileData.value.registration_token = reg_token;
};

// This function is called ONLY when user manually clicks "Check Activation" button
// after they have clicked the activation link in their email
const verifyAccountActivation = async () => {
  // console.log('verifyAccountActivation', profileData.value);
  if (!profileData.value.email || !profileData.value.registration_token) {
    activationError.value = t('registration.step_account_verification.missingIdentifierOrRegistrationToken');
    return;
  }
  
  // Set loading state
  isCheckingActivation.value = true;
  activationError.value = '';
  activationInformation.value = '';
  
  try {
    // Set the account type for account login

    if (DEBUG_MODE) {
      console.log('=== ACTIVATION DEBUG ===');
      console.log('Account type:', r7eBaseApp.account_type);
      console.log('Username:', profileData.value.username);
      console.log('Checking account activation status...');
    }
    // Check account activation status
    const activationStatus = await r7eBaseApp.checkAccountActivation(
      profileData.value.email, 
      profileData.value.registration_token
    );
    if (DEBUG_MODE) {
      console.log('Activation status:', activationStatus);
      console.log('=========================');
    }
    
    if (activationStatus.activated) {
      // Account is activated, now do full login
      if (profileData.value.username && profileData.value.password) {
        const loginResult = await r7eBaseApp.loginAccount(
          profileData.value.username, 
          profileData.value.password
        );
        
        if (loginResult) {
          // console.log('Login successful, proceeding to Claim Resources');
          activationSuccess.value = true;
          activationError.value = '';
          
          // Show success message briefly, then proceed
          setTimeout(() => {
            goToRegistrationComplete();
          }, 1500);
        } else {
          // Login failed despite activation check passing
          activationError.value = 'Login failed after activation check. Please try again.';
        }
      } else {
        goToRegistrationComplete();       
      }
    } else {
      // Account not yet activated, show appropriate error
      if (activationStatus.error) {
        activationError.value = activationStatus.error;
      } else if (activationStatus.status === 'pending') {
        activationError.value = t('registration.step_account_verification.accountNotYetActivated');
      } else if (activationStatus.status === 'error') {
        activationError.value = t('registration.step_account_verification.accountActivationCheckFailed');
      } else if (activationStatus.status === 'rejected') {
        activationError.value = t('registration.step_account_verification.accountRegistrationRejected');
      } else if (!activationStatus.hasJwtLink) {
        activationError.value = t('registration.step_account_verification.accountNotYetActivated');
      } else {
        activationError.value = t('registration.step_account_verification.accountActivationCheckFailed');
      }
    }
  } catch (error) {
    console.error('Activation failed:', error);
    
    // Handle specific error types
    if (error.response?.status === 401) {
      // Unauthorized - account not activated
      activationError.value = t('registration.step_account_verification.accountNotYetActivated');
    } else if (error.response?.status === 400) {
      // Bad request - invalid credentials
      activationError.value = t('registration.step_account_verification.invalidCredentials');
    } else if (error.message) {
      // Other errors
      activationError.value = error.message;
    } else {
      // Fallback error
      activationError.value = t('registration.step_account_verification.failedToVerifyAccountActivation');
    }
  } finally {
    isCheckingActivation.value = false;
  }
};


// Auto-check activation every 30s while on verification step
const verificationIntervalId = ref(null);

const clearVerificationInterval = () => {
  if (verificationIntervalId.value) {
    clearInterval(verificationIntervalId.value);
    verificationIntervalId.value = null;
  }
};

const maybeStartVerificationInterval = () => {
  clearVerificationInterval();
  if (currentStep.value === 'account_verification') {
    verificationIntervalId.value = setInterval(async () => {
      if (activationSuccess.value || currentStep.value !== 'account_verification') {
        clearVerificationInterval();
        return;
      }
      try {
        await verifyAccountActivation();
        if (activationSuccess.value) {
          clearVerificationInterval();
        }
      } catch (e) {
        // errors are handled inside verifyAccountActivation
      }
    }, 30000);
  }
};

watch(currentStep, () => {
  maybeStartVerificationInterval();
});

onMounted(() => {
  maybeStartVerificationInterval();
});

onUnmounted(() => {
  clearVerificationInterval();
});

const resendActivationEmail = async () => {
  isResendingActivation.value = true;
  activationError.value = '';
  activationInformation.value = '';
  
  try {
    // Call backend to resend activation email
    const result = await r7eBaseApp.resendActivationEmail(profileData.value.email);
    
    if (result && (result.success !== false)) {
      activationError.value = '';
      // Show success message in activation information box
      activationInformation.value = t('registration.step_account_verification.activationEmailResent');
      activationInformationType.value = 'success';
      // Clear the message after 5 seconds
      setTimeout(() => {
        activationInformation.value = '';
      }, 5000);
    } else {
      activationInformation.value = result?.message || t('registration.step_account_verification.failedToResendActivationEmail');
      activationInformationType.value = 'error';
      // Clear the error message after 5 seconds
      setTimeout(() => {
        activationInformation.value = '';
      }, 5000);
    }
  } catch (error) {
    console.error('Failed to resend activation email:', error);
    const errorMessage = error.response?.data?.message || t('registration.step_account_verification.failedToResendActivationEmail');
    activationInformation.value = errorMessage;
    activationInformationType.value = 'error';
    // Clear the error message after 5 seconds
    setTimeout(() => {
      activationInformation.value = '';
    }, 5000);
  } finally {
    isResendingActivation.value = false;
  }
};

const redirectToDashboard = () => {
  // Clear stored registration data on successful completion
  clearStoredRegistrationData();
  router.push('/');
};
const redirectToLogin = () => {
  clearStoredRegistrationData();
  // Pass email as query parameter to prefill login form
  const email = profileData.value.email;
  if (email) {
    router.push(`/login?email=${encodeURIComponent(email)}`);
  } else {
    router.push('/login');
  }
};
</script>

<style scoped>

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background-color: #f9f9f9;
  border: 1px solid var(--color-border);
  border-radius: 6px;
}
.summary-label {
  font-weight: bold;
  color: var(--color-text-dark);
}
.summary-value {
  color: var(--color-text);
}
.registration-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: var(--color-frame-bg);
  min-height: calc(100vh - 100px);
}

.registration-alternatives {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  justify-content: center;
  align-items: flex-start;
}

.registration-alternatives-login {
  margin-top: 2rem;
  text-align: center;
}

/* Resume Dialog */
.resume-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.resume-dialog {
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
  text-align: center;
}

.resume-dialog h3 {
  margin: 0 0 15px 0;
  color: var(--color-text-dark);
}

.resume-dialog p {
  margin: 0 0 25px 0;
  color: var(--color-text);
  line-height: 1.5;
}

.dialog-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.registration-header {
  text-align: center;
  margin-bottom: 40px;
}

.registration-header h1 {
  color: var(--color-text-dark);
  margin-bottom: 30px;
}

.progress-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  flex-wrap: nowrap;
  gap: 5px;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.5;
  margin: 0 2px;
  min-width: 0;
  flex-shrink: 1;
}

.progress-step.active {
  opacity: 1;
  color: var(--color-safe);
}

.progress-step.completed {
  opacity: 1;
  color: var(--color-safe);
}



/* Step numbers removed - showing only labels */

.progress-line {
  width: 40px;
  height: 2px;
  background-color: var(--color-border);
  margin: 0 5px;
  flex-shrink: 0;
}

.progress-line.completed {
  background-color: var(--color-safe);
}

.step-label {
  font-size: 0.8em;
  text-align: center;
  max-width: 90px;
  line-height: 1.2;
  word-wrap: break-word;
  hyphens: auto;
}

.step-label.clickable {
  cursor: pointer;
}

.registration-content {
  background-color: --color-bg;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.step-content h2 {
  color: var(--color-text-dark);
  margin-bottom: 20px;
}

.step-description {
  color: var(--color-text);
  margin-bottom: 30px;
  font-size: 1.1em;
}

.warning-message, .note-message {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.warning-icon, .info-icon {
  font-size: 1.2em;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 25px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
  color: var(--color-text-dark);
}

.email-input-wrapper {
  position: relative;
  width: 100%;
}

.email-input-wrapper .email-input {
  width: 100%;
  padding: 12px 40px 12px 12px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 1em;
  box-sizing: border-box;
  transition: border-color 0.3s, border-width 0.3s;
}

.email-input-wrapper .email-input.input-error {
  border-color: var(--color-danger, #e74c3c);
  border-width: 2px;
}

.email-input-wrapper .email-input.input-valid {
  border-color: var(--color-safe, #27ae60);
  border-width: 2px;
}

.email-validation-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.email-validation-icon .icon-valid {
  color: var(--color-safe, #27ae60);
}

.email-validation-icon .icon-invalid {
  color: var(--color-danger, #e74c3c);
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 1em;
  box-sizing: border-box;
}

.password-input {
  position: relative;
}

.password-input .toggle-visibility {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: var(--color-safe);
  cursor: pointer;
  font-weight: 600;
}

.password-input input {
  padding-right: 48px;
}

.password-input .toggle-visibility svg {
  width: 18px;
  height: 18px;
  display: block;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--color-safe);
}

.form-group input.invalid {
  border-color: var(--color-danger);
}

.search-section {
  margin-bottom: 30px;
}

.search-controls {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.search-input-group,
.url-input-group,
.root-url-input-group {
  display: flex;
  gap: 10px;
}

.search-input,
.url-input,
.root-url-input {
  flex: 1;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  font-size: 1em;
}

.search-btn,
.add-btn,
.discover-btn {
  padding: 12px 20px;
  background-color: var(--color-safe);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1em;
  white-space: nowrap;
}

.discover-btn {
  background-color: #17a2b8;
}

.search-btn:hover,
.add-btn:hover {
  background-color: var(--color-text-dark);
}

.discover-btn:hover {
  background-color: #138496;
}

.search-btn:disabled,
.add-btn:disabled,
.discover-btn:disabled {
  background-color: var(--color-border);
  cursor: not-allowed;
}

.discovery-description {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 10px;
  background-color: #e8f4f8;
  border-radius: 4px;
  font-size: 0.9em;
  color: var(--color-text);
}

.info-icon {
  font-size: 1.1em;
}

.search-results,
.discovered-resources,
.claimed-articles {
  margin-bottom: 30px;
}

.search-results h3,
.discovered-resources h3,
.claimed-articles h3 {
  color: var(--color-text-dark);
  margin-bottom: 15px;
}

.articles-list,
.articles-basket,
.discovered-articles-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.article-item,
.claimed-article-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background-color: #f9f9f9;
}

.article-info {
  flex: 1;
}

.article-info h4 {
  margin: 0 0 8px 0;
  color: var(--color-text-dark);
}

.article-url {
  color: var(--color-safe);
  font-size: 0.9em;
  margin: 0 0 8px 0;
}

.article-description {
  color: var(--color-text);
  font-size: 0.9em;
  margin: 0 0 8px 0;
}

.publisher-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.publisher-name {
  font-weight: bold;
  color: var(--color-text-dark);
}



/* Enhanced status styling */
.publisher-status {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.8em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.publisher-status.recognized,
.publisher-status.recognyzed {
  background-color: var(--color-safe);
  color: white;
  border: 2px solid var(--color-safe);
}

.publisher-status.recognized:hover,
.publisher-status.recognyzed:hover {
  background-color: #218838;
  border-color: #218838;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.publisher-status.unrecognized,
.publisher-status.unrecognyzed {
  background-color: var(--color-danger);
  color: white;
  border: 2px solid var(--color-danger);
}

.publisher-status.unrecognized:hover,
.publisher-status.unrecognyzed:hover {
  background-color: #c82333;
  border-color: #c82333;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Status indicator icons */
.publisher-status.recognized::before,
.publisher-status.recognyzed::before {
  content: "✓ ";
  margin-right: 4px;
  font-weight: bold;
}

.publisher-status.unrecognized::before,
.publisher-status.unrecognyzed::before {
  content: "⚠ ";
  margin-right: 4px;
  font-weight: bold;
}

/* Status summary styling */
.status-summary {
  display: flex;
  gap: 20px;
  margin: 20px 0;
  flex-wrap: wrap;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}

.status-item.recognized,
.status-item.recognyzed {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.status-item.unrecognized,
.status-item.unrecognyzed {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

/* Utility classes for status indicators */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.8em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
}

.status-badge.recognized,
.status-badge.recognyzed {
  background-color: var(--color-safe);
  color: white;
  border: 2px solid var(--color-safe);
}

.status-badge.unrecognized,
.status-badge.unrecognyzed {
  background-color: var(--color-danger);
  color: white;
  border: 2px solid var(--color-danger);
}

/* Status in different contexts */
.publisher-info .publisher-status {
  margin-left: auto;
}

.resource-meta .publisher-status {
  margin-top: 4px;
}

/* Hover effects for interactive status elements */
.publisher-status:hover,
.status-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Enhanced table styling for publisher status */
.publishers-table .publisher-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  text-align: center;
  /* Match btn-primary styling */
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 1em;
  font-weight: bold;
  text-transform: lowercase;
}

/* Ensure color styling applies to table status */
.publishers-table .publisher-status.recognized,
.publishers-table .publisher-status.recognyzed {
  background-color: var(--color-safe);
  color: white;
  border: 2px solid var(--color-safe);
}

.publishers-table .publisher-status.unrecognized,
.publishers-table .publisher-status.unrecognyzed {
  background-color: var(--color-danger);
  color: white;
  border: 2px solid var(--color-danger);
}

/* Ensure icons appear in table status */
.publishers-table .publisher-status.recognized::before,
.publishers-table .publisher-status.recognyzed::before {
  content: "✓ ";
  margin-right: 4px;
  font-weight: bold;
}

.publishers-table .publisher-status.unrecognized::before,
.publishers-table .publisher-status.unrecognyzed::before {
  content: "⚠ ";
  margin-right: 4px;
  font-weight: bold;
}

/* Hover effects for table status */
.publishers-table .publisher-status.recognized:hover,
.publishers-table .publisher-status.recognyzed:hover {
  background-color: #218838;
}

.publishers-table .publisher-status.unrecognized:hover,
.publishers-table .publisher-status.unrecognyzed:hover {
  background-color: #c82333;
}

/* Status in article items */
.article-item .publisher-status,
.discovered-article-item .publisher-status {
  align-self: flex-start;
  margin-top: 8px;
}

/* Status in claimed articles */
.claimed-article-item .publisher-status {
  margin-left: auto;
  align-self: center;
}

/* Status in different table contexts */
.table-row .publisher-status {
  justify-self: center;
  align-self: center;
}

/* Status with contact information */
.contact-info + .publisher-status {
  margin-top: 4px;
}

/* Status in publisher stats */
.publisher-stats .stat .publisher-status {
  margin-top: 8px;
  font-size: 0.9em;
}

/* Status in search results */
.search-results .publisher-status {
  margin-top: 6px;
}

/* Status in discovered resources */
.discovered-articles-list .publisher-status {
  margin-top: 6px;
}

/* Responsive status display */
@media (max-width: 768px) {
  .publisher-status {
    font-size: 0.75em;
    padding: 3px 8px;
  }
  
  .status-summary {
    flex-direction: column;
    gap: 10px;
  }
  
  .status-item {
    justify-content: center;
  }
  
  .status-badge {
    font-size: 0.7em;
    padding: 3px 8px;
  }
  
  .publisher-info .publisher-status {
    margin-left: 0;
    margin-top: 8px;
    align-self: flex-start;
  }
  
  .resource-meta .publisher-status {
    margin-top: 6px;
  }
}

@media (max-width: 480px) {
  .publisher-status,
  .status-badge {
    font-size: 0.65em;
    padding: 2px 6px;
    border-radius: 12px;
  }
  
  .status-summary {
    gap: 8px;
  }
  
  .status-item {
    padding: 6px 12px;
    font-size: 0.9em;
  }
}

.claim-btn,
.remove-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
}

.claim-btn {
  background-color: var(--color-safe);
  color: white;
}

.claim-btn:disabled {
  background-color: var(--color-border);
  cursor: not-allowed;
}

.remove-btn {
  background-color: var(--color-danger);
  color: white;
}

/* Discovered Resources Styles */
.discovery-info {
  background-color: #e8f4f8;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.discovery-info p {
  margin: 5px 0;
  color: var(--color-text-dark);
}

.discovery-method {
  font-style: italic;
  color: var(--color-text);
}

.estimated-total {
  font-weight: bold;
  color: var(--color-safe);
}

.bulk-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.bulk-btn,
.claim-selected-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
}

.bulk-btn {
  background-color: #6c757d;
  color: white;
}

.bulk-btn:hover {
  background-color: #5a6268;
}

.bulk-btn:disabled {
  background-color: var(--color-border);
  cursor: not-allowed;
}

.claim-selected-btn {
  background-color: var(--color-safe);
  color: white;
  font-weight: bold;
}

.claim-selected-btn:hover {
  background-color: var(--color-text-dark);
}

.claim-selected-btn:disabled {
  background-color: var(--color-border);
  cursor: not-allowed;
}

.discovered-article-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background-color: #f9f9f9;
  transition: all 0.2s ease;
}

.discovered-article-item.selected {
  border-color: var(--color-safe);
  background-color: #e8f5e8;
}

.selection-checkbox {
  flex-shrink: 0;
}

.selection-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.resource-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.resource-type {
  background-color: #17a2b8;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: bold;
}

.repeat-actions {
  justify-content: center;
  gap: 20px;
}

.current-claims {
  background-color: #e8f5e8;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  text-align: center;
}

.discovering {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding: 40px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid var(--color-safe);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.publisher-discovery-results {
  margin-bottom: 30px;
}

.publishers-table,
.claims-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 2fr 2fr 1.2fr 1.2fr 1.2fr;
  gap: 20px;
  padding: 12px;
  border-radius: 4px;
}

.table-header {
  background-color: var(--color-frame-bg);
  font-weight: bold;
  color: var(--color-text-dark);
}

.table-row {
  background-color: #f9f9f9;
  border: 1px solid var(--color-border);
}

.articles-count {
  text-align: center;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.contact-status {
  font-size: 0.8em;
  color: var(--color-text);
}

.contact-status.found {
  color: var(--color-safe);
  font-weight: bold;
}

.publisher-stats {
  display: flex;
  gap: 20px;
  padding: 15px;
  background-color: var(--color-frame-bg);
  border-radius: 6px;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.stat-label {
  font-size: 0.9em;
  color: var(--color-text);
}

.stat-value {
  font-size: 1.2em;
  font-weight: bold;
  color: var(--color-text-dark);
}

.claim-status-section {
  margin-bottom: 30px;
}

.claims-table .table-header,
.claims-table .table-row {
  grid-template-columns: 2fr 1.5fr 1fr 1fr 2fr;
}

.claim-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: bold;
  text-align: center;
}

.claim-status.accepted {
  background-color: var(--color-safe);
  color: white;
}

.claim-status.rejected {
  background-color: var(--color-danger);
  color: white;
}

.claim-status.pending {
  background-color: #ffc107;
  color: #212529;
}

.claim-status.no_response {
  background-color: #6c757d;
  color: white;
}

.status-summary {
  display: flex;
  gap: 20px;
  justify-content: center;
  margin: 20px 0;
}


.action-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin: 20px 0;
}

.publisher-notification {
  margin-bottom: 30px;
}

.publisher-notification h3 {
  color: var(--color-text-dark);
  margin-bottom: 15px;
}

.publishers-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.publisher-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background-color: #f9f9f9;
  border: 1px solid var(--color-border);
  border-radius: 6px;
}

.publisher-name {
  font-weight: bold;
  color: var(--color-text-dark);
}

.publisher-email {
  background-color: var(--color-safe);
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 0.9em;
}

.notification-description {
  color: var(--color-text);
  font-style: italic;
  text-align: center;
  margin-top: 15px;
}

.no-articles-message {
  text-align: center;
  padding: 40px;
  color: var(--color-text);
}

.resend-activation-action {
  margin-top: 1rem;
}

.form-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 40px;
}

.btn-primary,
.btn-secondary {
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1em;
  font-weight: bold;
  transition: background-color 0.3s;
}

.btn-primary {
  background-color: var(--color-safe);
  color: var(--color-text);
}

.btn-primary:hover {
  background-color: var(--color-text);
  color: var(--color-safe);
}

.btn-primary:disabled {
  background-color: var(--color-border);
  cursor: not-allowed;
}

.btn-secondary {
  background-color: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-border);
}

.btn-secondary:hover {
  background-color: var(--color-border);
}

.completion {
  text-align: center;
}

.success-icon {
  font-size: 4em;
  color: var(--color-safe);
  margin-bottom: 20px;
}

.completion-summary {
  background-color: #f9f9f9;
  padding: 20px;
  border-radius: 6px;
  margin: 30px 0;
  text-align: left;
}

.completion-summary h3 {
  color: var(--color-text-dark);
  margin-bottom: 15px;
}

.completion-summary ul {
  list-style-type: none;
  padding: 0;
}

.completion-summary li {
  padding: 5px 0;
  color: var(--color-text);
}

.completion-summary li:before {
  content: "✓ ";
  color: var(--color-safe);
  font-weight: bold;
}

/* Account Verification Step Styles */
.verification-info {
  background-color: #e8f4f8;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #bee5eb;
}

.verification-info .info-icon {
  font-size: 1.2em;
}

.activation-information {
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: fadeInUp 0.4s ease-out;
}

.activation-information .info-icon {
  font-size: 1.2em;
  font-weight: bold;
  flex-shrink: 0;
}

.activation-information p {
  margin: 0;
  flex: 1;
}

.activation-information-success {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.activation-information-success .info-icon {
  color: #155724;
}

.activation-information-error {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.activation-information-error .info-icon {
  color: #721c24;
}

.checking-activation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 20px;
  border: 1px solid #dee2e6;
}



.sending-token {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  padding: 40px;
  background-color: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 20px;
}

.verification-form {
  margin-top: 20px;
}

.token-input {
  text-align: center;
  font-size: 1.4em;
  letter-spacing: 0.3em;
  font-family: 'Courier New', Consolas, monospace;
  padding: 20px 15px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  background-color: #fafafa;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 200px;
  margin: 0 auto;
  display: block;
}

.token-input:focus {
  outline: none;
  border-color: var(--color-safe);
  background-color: white;
  box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
}

.token-input:disabled {
  background-color: #e9ecef;
  color: #6c757d;
  cursor: not-allowed;
}

.field-help {
  display: block;
  margin-top: 5px;
  font-size: 0.85em;
  color: var(--color-text);
  font-style: italic;
}


.success-message {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 15px;
  border-radius: 6px;
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
}

.success-message .success-icon {
  font-size: 1.2em;
}

.error-message {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 15px;
  border-radius: 6px;
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.error-message .error-icon {
  font-size: 1.2em;
}

.input-error {
  color: #721c24;
  background: transparent;
  font-size: 0.9em;
  margin-top: 6px;
}

.attempts-remaining {
  font-size: 0.9em;
  font-style: italic;
  margin-left: auto;
}

.resend-section {
  text-align: center;
  margin: 30px 0;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.resend-text {
  color: var(--color-text);
  margin-bottom: 15px;
  font-size: 0.95em;
}

.resend-btn {
  padding: 10px 20px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.3s ease;
}

.resend-btn:hover:not(:disabled) {
  background-color: #5a6268;
}

.resend-btn:disabled {
  background-color: #adb5bd;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Enhanced progress step for verification */
/* Special step 1.5 styling removed - showing only labels */

/* Verification step animations */
.verification-form {
  animation: fadeInUp 0.4s ease-out;
}

.success-message {
  animation: slideInRight 0.5s ease-out;
}

.error-message {
  animation: shake 0.5s ease-in-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

/* Loading states */
.token-input.loading {
  position: relative;
}

.token-input.loading::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: 2px solid var(--color-border);
  border-top: 2px solid var(--color-safe);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@media (min-width: 1401px) {
  .registration-container {
    max-width: 1400px;
  }
  
  .progress-step {
    margin: 0 8px;
  }
  
  .progress-line {
    width: 80px;
    margin: 0 12px;
  }
  
  .step-label {
    font-size: 0.9em;
    max-width: 110px;
  }
}

@media (max-width: 1024px) and (min-width: 769px) {
  .progress-step {
    margin: 0 1px;
  }
  
  .progress-line {
    width: 30px;
    margin: 0 3px;
  }
  
  .step-label {
    font-size: 0.75em;
    max-width: 70px;
  }
}

@media (max-width: 768px) {
  .registration-container {
    padding: 10px;
  }
  
  .registration-content {
    padding: 20px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .progress-bar {
    flex-direction: column;
    gap: 15px;
  }
  
  .progress-line {
    width: 2px;
    height: 30px;
    margin: 0;
  }
  
  .search-controls {
    gap: 10px;
  }
  
  .search-input-group,
  .url-input-group,
  .root-url-input-group {
    flex-direction: column;
  }
  
  .article-item,
  .claimed-article-item,
  .discovered-article-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .bulk-actions {
    flex-direction: column;
    gap: 8px;
  }
  
  .resource-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .table-header,
  .table-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .table-header span,
  .table-row span {
    padding: 4px 0;
  }
  
  .status-summary {
    flex-wrap: wrap;
    gap: 10px;
  }
  
  .form-actions {
    flex-direction: column;
    gap: 15px;
  }
  
  .repeat-actions {
    flex-direction: column;
  }
  
  .publisher-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .publisher-email {
    align-self: flex-end;
  }
  
  /* Mobile verification step styles */
  .verification-info {
    padding: 12px;
    font-size: 0.9em;
  }
  
  .token-input {
    font-size: 1.2em;
    padding: 15px 10px;
    max-width: 100%;
  }
  
  .resend-section {
    padding: 15px;
    margin: 20px 0;
  }
  
  .success-message,
  .error-message {
    padding: 12px;
    font-size: 0.9em;
  }
  
  .sending-token {
    padding: 30px 15px;
    flex-direction: column;
    gap: 10px;
  }
}
</style>
